export type HomeLanguage = 'ko' | 'en' | 'ja' | 'cn'

const CES_AWARD_URL = 'https://www.ces.tech/ces-innovation-awards/2026/carearly/'
const EDISON_AWARD_URL = 'https://edisonawards.com/winner/clomental/'
const KOLAS_REPORT_URL = '/certificates/carearly-kolas-test-report-page-1.pdf'

export interface HomeCopy {
    heroEyebrow: string
    heroTitle: string
    heroAccent: string
    heroDescription: string
    heroPrimary: string
    heroSecondary: string
    heroNote: string
    heroVisualLabel: string
    heroImageAlt: string
    metricsLabel: string
    vestImageAlt: string
    wearingImageAlt: string
    featureImageAlt: (title: string) => string
    networkImageAlt: string
    metrics: Array<{ value: string; label: string }>
    metricsNote: string
    challengeEyebrow: string
    challengeTitle: string
    challengeDescription: string
    challenges: Array<{ number: string; title: string; description: string }>
    solutionQuote: string
    technologyEyebrow: string
    technologyTitle: string
    technologyDescription: string
    technologyPoints: string[]
    productCaption: string
    productSpecs: Array<{ value: string; label: string }>
    featuresEyebrow: string
    featuresTitle: string
    featuresDescription: string
    featureDescriptions: string[]
    operationsEyebrow: string
    operationsTitle: string
    operationsDescription: string
    operationSteps: Array<{ step: string; title: string; description: string }>
    evidenceEyebrow: string
    evidenceTitle: string
    evidenceDescription: string
    securityCertification: {
        eyebrow: string
        title: string
        description: string
        guide: string
        imageAlt: string
    }
    gsCertification: {
        eyebrow: string
        title: string
        description: string
        status: string
    }
    evidenceItems: Array<{
        value: string
        label: string
        description: string
        eyebrow?: string
        status?: string
        variant?: 'pending'
        links?: Array<{ label: string; href: string }>
    }>
    caseTitle: string
    caseMore: string
    ctaEyebrow: string
    ctaTitle: string
    ctaDescription: string
    ctaPrimary: string
    ctaSecondary: string
    disclaimer: string
}

const ko: HomeCopy = {
    heroEyebrow: '비침습 AI 시니어 케어 플랫폼',
    heroTitle: '돌봄의 빈틈을',
    heroAccent: '데이터로 연결합니다',
    heroDescription: '어르신에게 익숙한 의류형 디바이스로 실내외 일상의 라이프로그와 위치를 수집하고, 24시간 중앙관제센터가 생체 이상·긴급 버튼·낙상 이벤트를 확인해 연락과 119 대응을 연결합니다.',
    heroPrimary: '서비스 바로가기',
    heroSecondary: '도입 상담하기',
    heroNote: '카메라 없이 · 피부 직접 접촉 없이 · 기관 맞춤형 운영',
    heroVisualLabel: '케얼리 관제 서비스 화면과 착용 디바이스',
    heroImageAlt: '케얼리 통합 관제 화면, 지도와 스마트 조끼',
    metricsLabel: '케얼리 운영 성과',
    vestImageAlt: '케얼리 의류형 디바이스와 구동부',
    wearingImageAlt: '야외에서 케얼리 조끼를 착용한 모습',
    featureImageAlt: title => `${title} 화면`,
    networkImageAlt: '이용자, 보호자, 관리자와 대응 기관이 연결되는 돌봄 네트워크',
    metrics: [
        { value: '30+', label: '도입 지자체·복지기관' },
        { value: '2,000+', label: '서비스 이용 시니어' },
        { value: '8.02h', label: '일평균 자발적 착용' },
        { value: '99%', label: '서비스 유지율' }
    ],
    metricsNote: '케얼리 브로슈어 및 운영 자료 기준',
    challengeEyebrow: 'WHY CAREARLY',
    challengeTitle: '감시가 아닌, 존엄한 돌봄이 필요합니다',
    challengeDescription: '시니어 돌봄은 더 오래 보는 기술보다, 일상을 방해하지 않으면서 필요한 순간을 놓치지 않는 기술이어야 합니다.',
    challenges: [
        { number: '01', title: '착용의 불편', description: '손목형·패치형 기기의 불편과 피부 접촉은 장시간 데이터 공백으로 이어질 수 있습니다.' },
        { number: '02', title: '사생활 침해', description: '카메라 중심의 모니터링은 어르신의 거부감과 프라이버시 문제를 만듭니다.' },
        { number: '03', title: '관리자의 과부하', description: '적은 인력으로 여러 이용자의 상태와 위치, 이벤트를 동시에 확인하기 어렵습니다.' }
    ],
    solutionQuote: '케얼리는 의류형 비침습 센서와 통합 관제로 돌봄의 공백을 줄입니다.',
    technologyEyebrow: 'PNEUMATIC BCG',
    technologyTitle: '공기로 감지하는 미세한 심박의 움직임',
    technologyDescription: '자체 개발한 공기압-심탄도 센서가 공기셀을 통해 신체의 미세 진동을 감지합니다. 피부에 전극을 붙이지 않고도 심박·호흡 등 라이프로그를 연속적으로 수집할 수 있도록 설계했습니다.',
    technologyPoints: ['피부 비접촉 센싱', '의류형 자연스러운 착용', 'LTE 기반 실시간 전송', 'GPS·9축 IMU 통합'],
    productCaption: '조끼 본체 · 공기압 BCG 센서 · 구동부로 구성',
    productSpecs: [
        { value: '77g', label: '구동부 무게' },
        { value: 'LTE', label: '독립 통신' },
        { value: 'GNSS', label: '위치 수집' },
        { value: 'USB-C', label: '간편 충전' }
    ],
    featuresEyebrow: 'ONE CARE CONSOLE',
    featuresTitle: '필요한 돌봄 기능을 한 화면에',
    featuresDescription: '현장 담당자가 많은 화면을 오가지 않도록 라이프로그, 긴급 이벤트, 위치, 이용자 관리와 리포트를 하나의 운영 흐름으로 연결했습니다.',
    featureDescriptions: [
        '심박·호흡·걸음 등 시간대별 라이프로그와 변화 흐름을 직관적으로 확인합니다.',
        '긴급 버튼·낙상·생체 이상 이벤트 발생 시 대상자와 위치를 확인하고 대응 과정을 기록합니다.',
        '현재 위치와 이동 이력을 날짜별로 확인해 현장 대응을 지원합니다.',
        '이용자, 보호자, 디바이스, 배터리와 담당 정보를 한곳에서 관리합니다.',
        '누적 데이터를 바탕으로 건강·마음 상태의 변화 흐름을 리포트로 확인합니다.'
    ],
    operationsEyebrow: 'HOW IT WORKS',
    operationsTitle: '실내외 24시간 관제부터 119 대응까지',
    operationsDescription: '착용자의 일상 데이터와 긴급 이벤트를 중앙관제센터가 지속해서 확인하고, 상황에 따라 연락·보호자 연계·119 출동을 진행합니다.',
    operationSteps: [
        { step: '01', title: '실내외에서 착용합니다', description: '익숙한 조끼 형태로 24시간 심박·호흡·활동·위치 데이터를 자연스럽게 수집합니다.' },
        { step: '02', title: '이벤트를 자동 감지합니다', description: '생체신호 이상과 낙상을 감지하고, 착용자가 누른 긴급 버튼 신호를 즉시 전송합니다.' },
        { step: '03', title: '중앙관제센터가 확인합니다', description: '관제 담당자가 대상자 정보, 실시간 상태와 현재 위치를 한 화면에서 확인합니다.' },
        { step: '04', title: '착용자에게 연락합니다', description: '대상자에게 먼저 연락해 상태와 현장 상황을 확인하고 보호자·담당자에게 공유합니다.' },
        { step: '05', title: '필요 시 119를 연계합니다', description: '응급 상황으로 판단되면 위치와 상황을 전달해 119 출동을 요청하고 조치 내용을 남깁니다.' }
    ],
    evidenceEyebrow: 'TRUSTED CARE',
    evidenceTitle: '현장에서 검증하고, 기술로 증명했습니다',
    evidenceDescription: '공공 돌봄 현장의 운영 경험과 센서 원천 기술, 품질경영 체계를 기반으로 기관 도입을 지원합니다.',
    securityCertification: {
        eyebrow: 'IOT SECURITY CERTIFIED',
        title: 'IoT 보안인증 BASIC 획득',
        description: '케얼리 통신형 IoT 센서는 제품의 보안 요구사항을 확인하는 IoT 보안인증 BASIC을 획득했습니다.',
        guide: '인증 상세 정보 확인',
        imageAlt: 'Security Certified IoT BASIC 인증 로고 및 인증 확인 QR 코드'
    },
    gsCertification: {
        eyebrow: 'SOFTWARE QUALITY',
        title: 'GS 인증',
        description: '인증 완료 후 공식 GS 인증 로고와 상세 정보를 이 공간에 제공합니다.',
        status: '인증 정보 업데이트 예정'
    },
    evidenceItems: [
        { value: 'KOLAS', label: '시험 신뢰성', description: '공인 시험 기반의 센서 성능 검증', links: [{ label: '시험성적서 1페이지', href: KOLAS_REPORT_URL }] },
        { value: '18', label: '등록 특허', description: '공기압 심탄도 센서 관련 원천 기술' },
        { value: 'KC · FCC · CE', label: '제품 인증', description: '한국·미국·유럽 시장 기준의 제품 적합성 확보' },
        { value: 'ISO 9001', label: '품질경영', description: '국제 품질경영시스템 인증' },
        {
            value: '혁신시제품 예정',
            label: '공공조달',
            description: '조달청 혁신시제품 지정 신청 절차를 진행하고 있습니다.',
            eyebrow: 'PUBLIC PROCUREMENT',
            status: '지정 절차 진행 중',
            variant: 'pending'
        },
        { value: 'CES 2026', label: '혁신상', description: 'CES Innovation Awards 2026 수상', links: [{ label: 'CES 수상 확인', href: CES_AWARD_URL }] },
        { value: 'Edison', label: '에디슨 어워즈', description: 'Clomental의 혁신성과 사회적 가치 인정', links: [{ label: '수상 확인', href: EDISON_AWARD_URL }] }
    ],
    caseTitle: '공공 돌봄 현장의 케얼리',
    caseMore: '도입 소식 보기',
    ctaEyebrow: 'START WITH CAREARLY',
    ctaTitle: '더 많은 어르신을, 더 세심하게 돌보세요',
    ctaDescription: '기관 규모와 운영 방식에 맞는 디바이스 구성, 관제 범위, 연계 방안을 함께 설계해 드립니다.',
    ctaPrimary: '도입 상담하기',
    ctaSecondary: '서비스 체험하기',
    disclaimer: '케얼리는 안전 확인과 건강 흐름 관리를 지원하는 비의료 서비스입니다. 질병의 진단·치료·예방 및 의료적 판단을 제공하지 않습니다.'
}

const en: HomeCopy = {
    heroEyebrow: 'Non-invasive AI senior care platform',
    heroTitle: 'Connect every gap in care',
    heroAccent: 'with meaningful data',
    heroDescription: 'A familiar wearable collects indoor and outdoor lifelog and location data while the 24/7 monitoring center reviews vital, SOS and fall events and coordinates calls and emergency response.',
    heroPrimary: 'Open service',
    heroSecondary: 'Talk to sales',
    heroNote: 'No cameras · No skin-contact electrodes · Built for care organizations',
    heroVisualLabel: 'Carearly monitoring service screen and wearable device',
    heroImageAlt: 'Carearly integrated monitoring console with a map, and the smart vest',
    metricsLabel: 'Carearly operating results',
    vestImageAlt: 'Carearly wearable vest and controller',
    wearingImageAlt: 'A person wearing the Carearly vest outdoors',
    featureImageAlt: title => `${title} screen`,
    networkImageAlt: 'Care network connecting members, guardians, administrators and response organizations',
    metrics: [
        { value: '30+', label: 'Public and care organizations' },
        { value: '2,000+', label: 'Senior service users' },
        { value: '8.02h', label: 'Average daily wear time' },
        { value: '99%', label: 'Service retention' }
    ],
    metricsNote: 'Based on Carearly brochure and operational materials',
    challengeEyebrow: 'WHY CAREARLY',
    challengeTitle: 'Dignified care, not constant surveillance',
    challengeDescription: 'Senior care needs technology that respects daily life while helping teams notice the moments that matter.',
    challenges: [
        { number: '01', title: 'Wear discomfort', description: 'Wrist and patch devices can create discomfort, skin contact issues and gaps in long-term data.' },
        { number: '02', title: 'Privacy concerns', description: 'Camera-based monitoring can feel intrusive and compromise a senior’s dignity.' },
        { number: '03', title: 'Operational overload', description: 'Small teams struggle to follow the status, location and events of many people at once.' }
    ],
    solutionQuote: 'Carearly combines a non-invasive wearable sensor with one integrated care console.',
    technologyEyebrow: 'PNEUMATIC BCG',
    technologyTitle: 'Sensing subtle cardiac motion through air',
    technologyDescription: 'Our pneumatic ballistocardiography sensor detects micro-vibrations through an air cell. It is designed to collect continuous lifelog signals such as heart and respiratory activity without skin electrodes.',
    technologyPoints: ['No skin-contact electrodes', 'Familiar vest form', 'Real-time LTE transmission', 'Integrated GPS and 9-axis IMU'],
    productCaption: 'Vest · pneumatic BCG sensor · controller',
    productSpecs: [
        { value: '77g', label: 'Controller weight' },
        { value: 'LTE', label: 'Independent network' },
        { value: 'GNSS', label: 'Location data' },
        { value: 'USB-C', label: 'Simple charging' }
    ],
    featuresEyebrow: 'ONE CARE CONSOLE',
    featuresTitle: 'Everything your care team needs, together',
    featuresDescription: 'Lifelog trends, safety events, location, member management and reports are connected in one operational flow.',
    featureDescriptions: [
        'Review heart, respiration and step trends across the time range your team needs.',
        'Identify the person and location quickly when SOS or fall events occur.',
        'Review current location and daily movement history to support field response.',
        'Manage members, guardians, devices, batteries and assigned staff together.',
        'Turn accumulated data into easy-to-follow wellbeing and mind trend reports.'
    ],
    operationsEyebrow: 'HOW IT WORKS',
    operationsTitle: 'From 24/7 monitoring to emergency response',
    operationsDescription: 'The central monitoring center follows everyday data and safety events, contacts the wearer and coordinates guardians or emergency services when needed.',
    operationSteps: [
        { step: '01', title: 'Wear indoors and outdoors', description: 'The vest collects heart, respiration, activity and location data throughout daily life.' },
        { step: '02', title: 'Detect safety events', description: 'Vital anomalies, falls and manual SOS signals are sent to the Carearly platform.' },
        { step: '03', title: 'Monitor centrally', description: 'Operators review the member, live status and current location in one console.' },
        { step: '04', title: 'Contact the wearer', description: 'Operators call first to verify the situation and notify guardians or assigned staff.' },
        { step: '05', title: 'Coordinate emergency response', description: 'When required, operators share the situation and location with emergency services and record the action.' }
    ],
    evidenceEyebrow: 'TRUSTED CARE',
    evidenceTitle: 'Validated in the field, backed by technology',
    evidenceDescription: 'We support care organizations with public-sector experience, proprietary sensing technology and a certified quality-management framework.',
    securityCertification: {
        eyebrow: 'IOT SECURITY CERTIFIED',
        title: 'IoT Security Certification BASIC',
        description: 'The Carearly connected IoT sensor has obtained the BASIC level of IoT Security Certification for product security requirements.',
        guide: 'View certification details',
        imageAlt: 'Security Certified IoT BASIC certification mark and QR code to verify the certification'
    },
    gsCertification: {
        eyebrow: 'SOFTWARE QUALITY',
        title: 'GS Certification',
        description: 'The official GS certification mark and details will be added here once certification is complete.',
        status: 'Certification details coming soon'
    },
    evidenceItems: [
        { value: 'KOLAS', label: 'Testing confidence', description: 'Sensor performance assessed through accredited testing', links: [{ label: 'View report page 1', href: KOLAS_REPORT_URL }] },
        { value: '18', label: 'Registered patents', description: 'Proprietary pneumatic BCG sensor technology' },
        { value: 'KC · FCC · CE', label: 'Product compliance', description: 'Product conformity for the Korean, US and European markets' },
        { value: 'ISO 9001', label: 'Quality management', description: 'International quality-management system certification' },
        {
            value: 'Innovative Product Pending',
            label: 'Public procurement',
            description: 'The PPS Innovative Product designation application is in progress.',
            eyebrow: 'PUBLIC PROCUREMENT',
            status: 'Designation in progress',
            variant: 'pending'
        },
        { value: 'CES 2026', label: 'Innovation Award', description: 'CES Innovation Awards 2026 honoree', links: [{ label: 'View CES award', href: CES_AWARD_URL }] },
        { value: 'Edison', label: 'Edison Awards', description: 'Recognition of Clomental innovation and social value', links: [{ label: 'View award', href: EDISON_AWARD_URL }] }
    ],
    caseTitle: 'Carearly in public care',
    caseMore: 'Read the story',
    ctaEyebrow: 'START WITH CAREARLY',
    ctaTitle: 'Care for more seniors, with greater attention',
    ctaDescription: 'We help design the right device setup, monitoring scope and service integration for your organization.',
    ctaPrimary: 'Talk to sales',
    ctaSecondary: 'Try the service',
    disclaimer: 'Carearly is a non-medical service supporting safety checks and wellbeing trend management. It does not diagnose, treat or prevent disease and does not provide medical judgment.'
}

const ja: HomeCopy = {
    ...en,
    heroEyebrow: '非侵襲AIシニアケアプラットフォーム',
    heroTitle: 'ケアのすき間を',
    heroAccent: 'データでつなぎます',
    heroDescription: 'シニアになじみやすい衣類型デバイスで日常のライフログを収集し、安全イベント・位置・健康傾向を一つの管理画面で確認できます。',
    heroPrimary: 'サービスへ移動',
    heroSecondary: '導入相談',
    heroNote: 'カメラ不要 · 皮膚への電極接触なし · 施設別に最適化',
    heroVisualLabel: 'Carearly管制サービス画面と着用デバイス',
    heroImageAlt: 'Carearlyの統合管制画面、地図とスマートベスト',
    metricsLabel: 'Carearlyの運用実績',
    vestImageAlt: 'Carearlyの衣類型デバイスと制御部',
    wearingImageAlt: '屋外でCarearlyのベストを着用している様子',
    featureImageAlt: title => `${title}の画面`,
    networkImageAlt: '利用者、保護者、管理者、対応機関がつながるケアネットワーク',
    metrics: [
        { value: '30+', label: '導入自治体・福祉機関' },
        { value: '2,000+', label: 'サービス利用シニア' },
        { value: '8.02h', label: '1日平均自発的着用' },
        { value: '99%', label: 'サービス継続率' }
    ],
    metricsNote: 'Carearlyパンフレットおよび運用資料に基づく',
    challengeTitle: '監視ではなく、尊厳を守るケアを',
    challengeDescription: 'シニアケアに必要なのは、日常を妨げず、大切な瞬間を見逃さない技術です。',
    challenges: [
        { number: '01', title: '装着の負担', description: '腕時計型・パッチ型機器の不快感や皮膚接触は、長時間データの空白につながります。' },
        { number: '02', title: 'プライバシー', description: 'カメラ中心のモニタリングは、シニアの抵抗感や尊厳の問題を生みます。' },
        { number: '03', title: 'スタッフの負担', description: '少人数で多くの利用者の状態・位置・イベントを同時に把握するのは困難です。' }
    ],
    solutionQuote: 'Carearlyは、衣類型の非侵襲センサーと統合管理でケアの空白を減らします。',
    technologyTitle: '空気でとらえる、心拍の微細な動き',
    technologyDescription: '独自開発の空気圧式心弾動センサーが、エアセルを通じて身体の微細振動を検知します。皮膚に電極を付けず、心拍・呼吸などのライフログを連続収集できるよう設計されています。',
    technologyPoints: ['皮膚非接触センシング', '自然に着られるベスト型', 'LTEリアルタイム通信', 'GPS・9軸IMU内蔵'],
    productCaption: 'ベスト本体 · 空気圧BCGセンサー · 制御部',
    productSpecs: [
        { value: '77g', label: '制御部重量' },
        { value: 'LTE', label: '独立通信' },
        { value: 'GNSS', label: '位置収集' },
        { value: 'USB-C', label: '簡単充電' }
    ],
    featuresTitle: '必要なケア機能を一つの画面に',
    featuresDescription: 'ライフログ、安全イベント、位置、利用者管理、レポートを一つの運用フローにつなぎます。',
    featureDescriptions: [
        '心拍・呼吸・歩数など、必要な時間帯のライフログ傾向を直感的に確認できます。',
        'SOSや転倒などのイベント発生時、対象者と位置をすばやく確認します。',
        '現在地と日別移動履歴を確認し、現場対応を支援します。',
        '利用者、保護者、デバイス、バッテリー、担当情報を一元管理します。',
        '蓄積データから健康・こころの状態変化をレポートで確認します。'
    ],
    operationsTitle: '屋内外24時間の見守りから緊急対応まで',
    operationsDescription: '中央管制センターが日常データと緊急イベントを継続確認し、本人への連絡、保護者連携、必要時の救急要請につなげます。',
    operationSteps: [
        { step: '01', title: '屋内外で着用', description: '心拍・呼吸・活動・位置を日常の中で継続収集します。' },
        { step: '02', title: 'イベントを検知', description: '生体異常、転倒、SOSボタンの信号をCarearlyへ送信します。' },
        { step: '03', title: '中央で確認', description: '管制担当者が対象者、状態、現在地を一画面で確認します。' },
        { step: '04', title: '本人へ連絡', description: 'まず本人に連絡して状況を確認し、保護者や担当者へ共有します。' },
        { step: '05', title: '救急対応を連携', description: '必要時は位置と状況を救急機関へ伝え、対応記録を残します。' }
    ],
    evidenceTitle: '現場で検証し、技術で証明',
    evidenceDescription: '公共ケアの運用経験、独自センサー技術、品質マネジメント体制に基づき導入を支援します。',
    securityCertification: {
        eyebrow: 'IOT SECURITY CERTIFIED',
        title: 'IoTセキュリティ認証 BASICを取得',
        description: 'Carearlyの通信型IoTセンサーは、製品のセキュリティ要件を確認するIoTセキュリティ認証BASICを取得しました。',
        guide: '認証詳細を確認',
        imageAlt: 'Security Certified IoT BASIC認証ロゴと認証確認用QRコード'
    },
    gsCertification: {
        eyebrow: 'SOFTWARE QUALITY',
        title: 'GS認証',
        description: '認証完了後、公式GS認証ロゴと詳細情報をこのスペースに掲載します。',
        status: '認証情報は後日更新予定'
    },
    evidenceItems: [
        { value: 'KOLAS', label: '試験信頼性', description: '公認試験に基づくセンサー性能検証', links: [{ label: '試験成績書1ページ目', href: KOLAS_REPORT_URL }] },
        { value: '18', label: '登録特許', description: '空気圧式心弾動センサー関連技術' },
        { value: 'KC · FCC · CE', label: '製品認証', description: '韓国・米国・欧州市場基準への製品適合' },
        { value: 'ISO 9001', label: '品質マネジメント', description: '国際品質マネジメントシステム認証' },
        {
            value: '革新試作品 予定',
            label: '公共調達',
            description: '韓国調達庁の革新試作品指定申請手続きを進めています。',
            eyebrow: 'PUBLIC PROCUREMENT',
            status: '指定手続き進行中',
            variant: 'pending'
        },
        { value: 'CES 2026', label: 'イノベーション賞', description: 'CES Innovation Awards 2026を受賞', links: [{ label: 'CES受賞を確認', href: CES_AWARD_URL }] },
        { value: 'Edison', label: 'Edison Awards', description: 'Clomentalの革新性と社会的価値を評価', links: [{ label: '受賞を確認', href: EDISON_AWARD_URL }] }
    ],
    caseTitle: '公共ケア現場のCarearly',
    caseMore: '導入事例を見る',
    ctaTitle: 'より多くのシニアを、よりきめ細かく',
    ctaDescription: '施設規模と運用方法に合うデバイス構成、管理範囲、連携方法をご提案します。',
    ctaPrimary: '導入相談',
    ctaSecondary: 'サービスを体験',
    disclaimer: 'Carearlyは安全確認と健康傾向の管理を支援する非医療サービスです。疾病の診断・治療・予防、医療判断は提供しません。'
}

const cn: HomeCopy = {
    ...en,
    heroEyebrow: '非侵入式 AI 老年照护平台',
    heroTitle: '用数据连接',
    heroAccent: '每一处照护空白',
    heroDescription: '通过老人熟悉的服装式设备采集日常生活数据，在一个管控界面中查看安全事件、位置和健康趋势。',
    heroPrimary: '前往服务',
    heroSecondary: '咨询引进方案',
    heroNote: '无需摄像头 · 无皮肤电极接触 · 适配机构运营',
    heroVisualLabel: 'Carearly 管控服务界面与穿戴设备',
    heroImageAlt: 'Carearly 综合管控界面，地图与智能背心',
    metricsLabel: 'Carearly 运营成果',
    vestImageAlt: 'Carearly 服装式设备与控制器',
    wearingImageAlt: '在户外穿着 Carearly 背心的样子',
    featureImageAlt: title => `${title}界面`,
    networkImageAlt: '连接使用者、监护人、管理员与响应机构的照护网络',
    metrics: [
        { value: '30+', label: '引进政府与福利机构' },
        { value: '2,000+', label: '老年服务使用者' },
        { value: '8.02h', label: '日均自愿穿戴时间' },
        { value: '99%', label: '服务续用率' }
    ],
    metricsNote: '依据 Carearly 宣传册及运营资料',
    challengeTitle: '需要的是有尊严的照护，而不是监视',
    challengeDescription: '老年照护技术应尊重日常生活，同时帮助工作人员不错过真正重要的时刻。',
    challenges: [
        { number: '01', title: '穿戴不适', description: '腕带和贴片设备的不适与皮肤接触问题，可能造成长期数据中断。' },
        { number: '02', title: '隐私顾虑', description: '以摄像头为中心的监控容易引起老人抵触，并带来隐私问题。' },
        { number: '03', title: '运营负担', description: '少量工作人员难以同时掌握多位使用者的状态、位置和事件。' }
    ],
    solutionQuote: 'Carearly 通过服装式非侵入传感器与综合管控，减少照护空白。',
    technologyTitle: '通过空气感知细微心搏振动',
    technologyDescription: '自主研发的气压式心冲击描记传感器通过气囊感知身体微振动，无需在皮肤粘贴电极即可持续采集心率、呼吸等生活数据。',
    technologyPoints: ['非皮肤接触感知', '自然舒适的背心形态', 'LTE 实时传输', '集成 GPS 与九轴 IMU'],
    productCaption: '背心本体 · 气压式 BCG 传感器 · 控制器',
    productSpecs: [
        { value: '77g', label: '控制器重量' },
        { value: 'LTE', label: '独立通信' },
        { value: 'GNSS', label: '位置采集' },
        { value: 'USB-C', label: '便捷充电' }
    ],
    featuresTitle: '将照护所需功能集中在一个界面',
    featuresDescription: '把生活数据、安全事件、位置、会员管理与报告连接为完整的运营流程。',
    featureDescriptions: [
        '直观查看心率、呼吸、步数等不同时间段的生活数据趋势。',
        '发生 SOS 或跌倒事件时，迅速确认对象及所在位置。',
        '查看当前位置和每日移动记录，为现场响应提供支持。',
        '集中管理使用者、监护人、设备、电量和负责人信息。',
        '根据累积数据，以报告形式查看健康和心理状态的变化趋势。'
    ],
    operationsTitle: '从室内外全天监护到紧急响应',
    operationsDescription: '中央监护中心持续查看日常数据和安全事件，联系佩戴者，并在必要时联动监护人与急救服务。',
    operationSteps: [
        { step: '01', title: '室内外穿戴', description: '在日常生活中持续采集心率、呼吸、活动与位置。' },
        { step: '02', title: '自动发现事件', description: '生命体征异常、跌倒和手动 SOS 信号会发送至 Carearly。' },
        { step: '03', title: '中央集中监护', description: '工作人员在同一界面确认对象、实时状态和当前位置。' },
        { step: '04', title: '联系佩戴者', description: '首先致电确认现场情况，并通知监护人或负责人。' },
        { step: '05', title: '联动急救响应', description: '必要时将位置和情况提供给急救服务并记录处理过程。' }
    ],
    evidenceTitle: '经现场验证，由技术证明',
    evidenceDescription: '依托公共照护运营经验、自主传感技术和质量管理体系，为机构引进提供支持。',
    securityCertification: {
        eyebrow: 'IOT SECURITY CERTIFIED',
        title: '获得IoT安全认证 BASIC',
        description: 'Carearly通信型IoT传感器已获得用于确认产品安全要求的IoT安全认证BASIC。',
        guide: '查看认证详情',
        imageAlt: 'Security Certified IoT BASIC 认证标志及认证确认二维码'
    },
    gsCertification: {
        eyebrow: 'SOFTWARE QUALITY',
        title: 'GS认证',
        description: '认证完成后，将在此处提供官方GS认证标志和详细信息。',
        status: '认证信息即将更新'
    },
    evidenceItems: [
        { value: 'KOLAS', label: '检测可信度', description: '基于认可检测的传感器性能验证', links: [{ label: '查看报告第1页', href: KOLAS_REPORT_URL }] },
        { value: '18', label: '授权专利', description: '气压式心冲击描记传感器相关技术' },
        { value: 'KC · FCC · CE', label: '产品认证', description: '符合韩国、美国及欧洲市场产品标准' },
        { value: 'ISO 9001', label: '质量管理', description: '国际质量管理体系认证' },
        {
            value: '创新试制品 预定',
            label: '公共采购',
            description: '韩国采购厅创新试制品认定申请流程正在进行中。',
            eyebrow: 'PUBLIC PROCUREMENT',
            status: '认定流程进行中',
            variant: 'pending'
        },
        { value: 'CES 2026', label: '创新奖', description: '获得CES Innovation Awards 2026奖项', links: [{ label: '查看CES奖项', href: CES_AWARD_URL }] },
        { value: 'Edison', label: 'Edison Awards', description: 'Clomental的创新性与社会价值获得认可', links: [{ label: '查看奖项', href: EDISON_AWARD_URL }] }
    ],
    caseTitle: '公共照护现场的 Carearly',
    caseMore: '查看引进案例',
    ctaTitle: '更细致地照护更多老人',
    ctaDescription: '根据机构规模与运营方式，共同设计设备配置、管控范围和系统联动方案。',
    ctaPrimary: '咨询引进方案',
    ctaSecondary: '体验服务',
    disclaimer: 'Carearly 是支持安全确认和健康趋势管理的非医疗服务，不用于疾病诊断、治疗或预防，也不提供医疗判断。'
}

export const getHomeCopy = (language?: string): HomeCopy => {
    const normalizedLanguage = language?.toLowerCase() || 'en'
    if (normalizedLanguage.startsWith('ko')) return ko
    if (normalizedLanguage.startsWith('ja')) return ja
    if (normalizedLanguage.startsWith('cn') || normalizedLanguage.startsWith('zh')) return cn
    return en
}
